import { Link } from "react-router-dom";
import { useEffect } from "react";

import { useTranslation } from "@/core/i18n";
import { useHidePricing, useIsAnonymousBrowsing } from "@/modules/anonymousBrowsing";
import { ImagePreloader } from "atoms/image-preloader/ImagePreloader";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right_small.svg";
import { BadgeTag } from "atoms/tag/BadgeTag";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { JustForYouTag } from "atoms/tag/JustForYouTag";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { useFeatureFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import { formatBillingPeriodInterval } from "utils/billingPeriod.helpers";

interface ShopMembershipListItemProps {
  title: string;

  /** List of included items in this shop item */
  includedList?: Array<string> | null;

  /** Item Image */
  imageUrl?: string | null;

  /** Tag for item (ie: Most Popular, New, etc.) */
  tag?: string | null;

  concerns?: Array<string> | null;

  /** Standard item cost (in smallest monetary unit) */
  price: number;

  /** One-time setup fee charged when starting the membership (in smallest monetary unit) */
  setupFeeInCents?: number;

  onClick?: () => void;
  to?: string;

  /** Max discount for Product */
  discount?: string | null;

  /** Custom color for tag (site wide promo) */
  customColor?: string | null;

  /** Hide benefits and price from users */
  guestMode?: boolean;

  /** Message to display for clicking into the detail view */
  detailMessage?: string | null;

  billingPeriodInterval?: string;
  billingPeriodIntervalCount?: number;

  /** Whether this item is AI recommended */
  isRecommended?: boolean;

  /** Optional: ID of the membership to enable prefetching */
  membershipId?: string;
}

export const ShopMembershipListItem = ({
  title,
  includedList,
  imageUrl,
  tag,
  concerns,
  price,
  setupFeeInCents,
  discount,
  customColor,
  guestMode,
  onClick,
  to,
  detailMessage,
  billingPeriodInterval,
  billingPeriodIntervalCount,
  isRecommended = false,
  membershipId,
}: ShopMembershipListItemProps) => {
  const { t } = useTranslation();
  const isAnonymousBrowsing = useIsAnonymousBrowsing();
  // `guestMode` says the viewer has no account; whether that hides prices depends on
  // the platform's anonymous-checkout setting, so never gate price display on it directly.
  const hidePricing = useHidePricing({ isGuest: !!guestMode });
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchMembershipPDP } = usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting || !membershipId) return;
    prefetchMembershipPDP(membershipId);
  }, [isIntersecting, membershipId, prefetchMembershipPDP]);

  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const missingImage = (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-2">
      <span className="text-center text-body3 text-secondary">
        {t("productCardLarge.imageComingSoon")}
      </span>
    </div>
  );
  return (
    <div
      ref={ref}
      className="group relative flex h-full cursor-pointer flex-col rounded-xl bg-white p-3 focus-within:outline focus-within:outline-2 focus-within:outline-offset-4 focus-within:outline-brand-color"
      onClick={to ? undefined : onClick}
    >
      <div className="flex flex-1 items-start gap-3 desk:flex-col desk:gap-0">
        <div className="relative h-32 w-24 shrink-0 overflow-hidden rounded-xl desk:aspect-[4/5] desk:h-auto desk:w-full">
          {imageUrl ? (
            <ImagePreloader
              url={imageUrl}
              lazy
              fade={150}
              preloadDistance="100px"
              className="h-full w-full"
              placeholder={<div className="h-full w-full animate-pulse bg-gray-100" />}
              errorPlaceholder={missingImage}
            >
              <img
                src={imageUrl}
                alt=""
                className="h-full w-full object-cover object-top motion-safe:transition-transform motion-safe:duration-300 motion-safe:desk:group-hover:scale-[1.025]"
              />
            </ImagePreloader>
          ) : (
            missingImage
          )}
        </div>
        <div className="min-w-0 flex-1 desk:w-full desk:pt-3">
          {(discount || tag || isRecommended) && (
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {isRecommended && <JustForYouTag />}
              {discount && (
                <DescriptiveTag
                  color={customColor ? "custom" : "primary"}
                  size="small"
                  customColor={customColor}
                >
                  {discount}
                </DescriptiveTag>
              )}
              {tag && <BadgeTag>{tag}</BadgeTag>}
            </div>
          )}
          <h3 className="mb-1 font-serif text-[22px] leading-[1.2] tracking-tight text-primary">
            {to ? (
              <Link
                to={to}
                className="after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none"
              >
                {title}
              </Link>
            ) : (
              title
            )}
          </h3>
          {concerns && concerns.length > 0 && (
            <div className="flex flex-wrap gap-2 pb-2">
              {concerns?.slice(0, 3).map((concern) => (
                <div key={concern}>
                  <DescriptiveTag color="secondary" size="medium">
                    {concern}
                  </DescriptiveTag>
                </div>
              ))}
            </div>
          )}
          {includedList && includedList.length > 0 && (
            <div className="pb-1 pt-1 text-body2 leading-snug text-secondary">
              <ul className="flex list-outside list-disc flex-col gap-1 pl-4">
                {includedList?.slice(0, 3).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-outline-grey pt-3">
        {hidePricing && (
          <div className="text-body2 text-primary">
            {isAnonymousBrowsing
              ? t("anonymousBrowsing.logInForPricing")
              : t("shopMembershipListItem.signUpToSeePrice")}
          </div>
        )}
        {!hidePricing && (
          <>
            <div className="text-body2 font-semibold text-primary">
              {isFlexibleMembershipsEnabled ? (
                <span>
                  {formatBillingPeriodInterval(
                    price,
                    billingPeriodInterval ?? null,
                    billingPeriodIntervalCount ?? null,
                  )}
                </span>
              ) : (
                <>
                  <span>{formatCurrency(price)}</span>
                  <span>{t("shopMembershipListItem.perMonth")}</span>
                </>
              )}
              {!!setupFeeInCents && (
                <div className="text-body3 font-normal text-secondary">
                  {t("shopMembershipListItem.enrollmentFee", {
                    amount: formatCurrency(setupFeeInCents),
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <div className="text-body2 font-semibold text-brand-color">
                {detailMessage || t("shopMembershipListItem.seeAllBenefits")}
              </div>
              <Icon svg={ChevronRight} size="small" color="brandColor" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
